import { useState } from 'react';
import { AlertCircle, RefreshCw, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useInquiries } from '../hooks/useInquiries';
import { useMarkAsRead, useReplyToInquiry, useMarkAsReplied } from '../hooks/useInquiries';
import { InquiryStatusBadge } from '../components/InquiryStatusBadge';
import { InquiryDetailPanel } from '../components/InquiryDetailPanel';
import { ListRefreshButton } from '../components/ListRefreshButton';
import type { InquirySummary, InquiryStatus } from '../types/inquiry';
import { formatDate } from '../utils/formatDate';
import { isAuthRequiredError } from '../utils/listRefreshState';

const Inquiries = () => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'badges', 'navigation']);

  // ── Filter state ─────────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | ''>('');
  const [emailFilter, setEmailFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  // ── Pagination state ─────────────────────────────────────────────────────
  const [page, setPage] = useState(0);

  // ── Panel state ──────────────────────────────────────────────────────────
  const [selectedInquiry, setSelectedInquiry] = useState<InquirySummary | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const hasActiveFilters = Boolean(statusFilter || emailFilter || dateFromFilter || dateToFilter);

  const clearFilters = () => {
    setStatusFilter('');
    setEmailFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setPage(0);
  };

  // ── Data fetching ────────────────────────────────────────────────────────
  const {
    data,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useInquiries(
    {
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(emailFilter ? { submitterEmail: emailFilter } : {}),
      ...(dateFromFilter ? { createdAtFrom: `${dateFromFilter}T00:00:00Z` } : {}),
      ...(dateToFilter ? { createdAtTo: `${dateToFilter}T23:59:59Z` } : {}),
    },
    page,
  );

  // ── Mutations ────────────────────────────────────────────────────────────
  const { mutate: markAsRead, isPending: isMarkingAsRead } = useMarkAsRead();
  const { mutate: replyToInquiry, isPending: isReplying } = useReplyToInquiry();
  const { mutate: markAsReplied, isPending: isMarkingAsReplied } = useMarkAsReplied();

  const inquiries = data?.content ?? [];
  const totalPages = data?.totalPages ?? 1;

  const handleRefresh = async () => {
    try {
      setRefreshError(null);
      await refetch({ throwOnError: true });
    } catch (error) {
      setRefreshError(isAuthRequiredError(error) ? t('common:signInRequired') : t('common:refreshFailed'));
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('pages:inquiriesHeading')}</h1>
        <p className="text-gray-600 mt-2">{t('pages:inquiriesSubHeading')}</p>
      </div>

      <div className="flex justify-end mb-4">
        <ListRefreshButton isRefreshing={isFetching} onRefresh={handleRefresh} />
      </div>

      {refreshError && (
        <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <AlertCircle size={18} className="shrink-0" />
          <span className="flex-1 text-sm">{refreshError}</span>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1 text-sm font-medium hover:text-red-900 transition-colors"
          >
            <RefreshCw size={14} />
            {t('common:retry')}
          </button>
        </div>
      )}

      {/* ── Error banner ─────────────────────────────────────────────────── */}
      {isError && (
        <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <AlertCircle size={18} className="shrink-0" />
          <span className="flex-1 text-sm">{t('errors:failedToLoadInquiries')}</span>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1 text-sm font-medium hover:text-red-900 transition-colors"
          >
            <RefreshCw size={14} />
            {t('common:retry')}
          </button>
        </div>
      )}

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3 mb-6">
        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={e => {
            setStatusFilter(e.target.value as InquiryStatus | '');
            setPage(0);
          }}
          className="filter-control"
          aria-label={t('common:allStatuses')}
        >
          <option value="">{t('common:allStatuses')}</option>
          <option value="new">{t('badges:statusNew')}</option>
          <option value="read">{t('badges:statusRead')}</option>
          <option value="replied">{t('badges:statusReplied')}</option>
        </select>

        {/* Email filter */}
        <input
          type="text"
          value={emailFilter}
          onChange={e => {
            setEmailFilter(e.target.value);
            setPage(0);
          }}
          placeholder={t('pages:columnSubmitterEmail')}
          className="filter-control"
          aria-label={t('pages:columnSubmitterEmail')}
        />

        {/* Date-from filter */}
        <input
          type="date"
          value={dateFromFilter}
          onChange={e => {
            setDateFromFilter(e.target.value);
            setPage(0);
          }}
          className="filter-control"
          aria-label="Date from"
        />

        {/* Date-to filter */}
        <input
          type="date"
          value={dateToFilter}
          onChange={e => {
            setDateToFilter(e.target.value);
            setPage(0);
          }}
          className="filter-control"
          aria-label="Date to"
        />

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <XCircle size={14} />
            {t('common:clearFilters')}
          </button>
        )}
      </div>

      {/* ── Loading ───────────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="card flex justify-center py-16">
          <span className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      {!isLoading && (
        <>
          {inquiries.length === 0 ? (
            <div className="card">
              <p className="text-gray-600 text-center py-12">
                {t('pages:noInquiriesMatchFilters')}
              </p>
            </div>
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500 text-xs uppercase tracking-wide">
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnSubmitterName')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnSubmitterEmail')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnMessage')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnStatus')}</th>
                    <th className="pb-3 font-medium">{t('pages:columnSubmitted')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {inquiries.map(inquiry => (
                    <tr
                      key={inquiry.inquiryId}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedInquiry(inquiry)}
                    >
                      <td className="py-3 pr-4 font-medium text-gray-900">
                        {inquiry.name ?? '—'}
                      </td>
                      <td className="py-3 pr-4 text-gray-700">{inquiry.email}</td>
                      <td className="py-3 pr-4 text-gray-600 max-w-xs truncate">
                        {inquiry.message}
                      </td>
                      <td className="py-3 pr-4">
                        <InquiryStatusBadge status={inquiry.status} />
                      </td>
                      <td className="py-3 text-gray-600 whitespace-nowrap">
                        {formatDate(inquiry.createdAt.slice(0, 10))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ─────────────────────────────────────────────── */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-600">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1 px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label={t('common:previousPage')}
              >
                <ChevronLeft size={14} />
                {t('common:previous')}
              </button>
              <span aria-label="pagination">
                {t('common:pageOf', { current: page + 1, total: totalPages })}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1 px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label={t('common:nextPage')}
              >
                {t('common:next')}
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Detail panel ──────────────────────────────────────────────────── */}
      {selectedInquiry && (
        <InquiryDetailPanel
          inquiry={selectedInquiry}
          isMarkingAsRead={isMarkingAsRead}
          isReplying={isReplying}
          isMarkingAsReplied={isMarkingAsReplied}
          onMarkAsRead={id => markAsRead(id)}
          onReplyToInquiry={(id, request) => replyToInquiry({ inquiryId: id, request })}
          onMarkAsReplied={id => markAsReplied(id)}
          onClose={() => setSelectedInquiry(null)}
        />
      )}
    </div>
  );
};

export default Inquiries;
